// Country-code + national-number phone field that emits an E.164 string
// (e.g. +919876543210) to its parent. Defaults to India (Prakash Software). The
// country list is a curated set of common dial codes — extend as needed.
//
// `isValidE164` mirrors the server's accepted format so callers can gate submit.

import { useState } from 'react';

export interface Country {
  iso: string;
  name: string;
  dial: string;
  flag: string;
}

// Curated common countries. iso is the unique option key; multiple countries
// may share a dial code (e.g. US/CA → 1) which is fine.
export const COUNTRIES: Country[] = [
  { iso: 'IN', name: 'India', dial: '91', flag: '🇮🇳' },
  { iso: 'US', name: 'United States', dial: '1', flag: '🇺🇸' },
  { iso: 'CA', name: 'Canada', dial: '1', flag: '🇨🇦' },
  { iso: 'GB', name: 'United Kingdom', dial: '44', flag: '🇬🇧' },
  { iso: 'AU', name: 'Australia', dial: '61', flag: '🇦🇺' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '971', flag: '🇦🇪' },
  { iso: 'SG', name: 'Singapore', dial: '65', flag: '🇸🇬' },
  { iso: 'DE', name: 'Germany', dial: '49', flag: '🇩🇪' },
  { iso: 'FR', name: 'France', dial: '33', flag: '🇫🇷' },
  { iso: 'NL', name: 'Netherlands', dial: '31', flag: '🇳🇱' },
  { iso: 'ES', name: 'Spain', dial: '34', flag: '🇪🇸' },
  { iso: 'IT', name: 'Italy', dial: '39', flag: '🇮🇹' },
  { iso: 'IE', name: 'Ireland', dial: '353', flag: '🇮🇪' },
  { iso: 'CH', name: 'Switzerland', dial: '41', flag: '🇨🇭' },
  { iso: 'SE', name: 'Sweden', dial: '46', flag: '🇸🇪' },
  { iso: 'NO', name: 'Norway', dial: '47', flag: '🇳🇴' },
  { iso: 'DK', name: 'Denmark', dial: '45', flag: '🇩🇰' },
  { iso: 'BE', name: 'Belgium', dial: '32', flag: '🇧🇪' },
  { iso: 'AT', name: 'Austria', dial: '43', flag: '🇦🇹' },
  { iso: 'PT', name: 'Portugal', dial: '351', flag: '🇵🇹' },
  { iso: 'PL', name: 'Poland', dial: '48', flag: '🇵🇱' },
  { iso: 'SA', name: 'Saudi Arabia', dial: '966', flag: '🇸🇦' },
  { iso: 'QA', name: 'Qatar', dial: '974', flag: '🇶🇦' },
  { iso: 'KW', name: 'Kuwait', dial: '965', flag: '🇰🇼' },
  { iso: 'BH', name: 'Bahrain', dial: '973', flag: '🇧🇭' },
  { iso: 'OM', name: 'Oman', dial: '968', flag: '🇴🇲' },
  { iso: 'ZA', name: 'South Africa', dial: '27', flag: '🇿🇦' },
  { iso: 'NZ', name: 'New Zealand', dial: '64', flag: '🇳🇿' },
  { iso: 'JP', name: 'Japan', dial: '81', flag: '🇯🇵' },
  { iso: 'CN', name: 'China', dial: '86', flag: '🇨🇳' },
  { iso: 'HK', name: 'Hong Kong', dial: '852', flag: '🇭🇰' },
  { iso: 'MY', name: 'Malaysia', dial: '60', flag: '🇲🇾' },
  { iso: 'ID', name: 'Indonesia', dial: '62', flag: '🇮🇩' },
  { iso: 'PH', name: 'Philippines', dial: '63', flag: '🇵🇭' },
  { iso: 'TH', name: 'Thailand', dial: '66', flag: '🇹🇭' },
  { iso: 'VN', name: 'Vietnam', dial: '84', flag: '🇻🇳' },
  { iso: 'BD', name: 'Bangladesh', dial: '880', flag: '🇧🇩' },
  { iso: 'PK', name: 'Pakistan', dial: '92', flag: '🇵🇰' },
  { iso: 'LK', name: 'Sri Lanka', dial: '94', flag: '🇱🇰' },
  { iso: 'NP', name: 'Nepal', dial: '977', flag: '🇳🇵' },
  { iso: 'BR', name: 'Brazil', dial: '55', flag: '🇧🇷' },
  { iso: 'MX', name: 'Mexico', dial: '52', flag: '🇲🇽' },
  { iso: 'AR', name: 'Argentina', dial: '54', flag: '🇦🇷' },
  { iso: 'IL', name: 'Israel', dial: '972', flag: '🇮🇱' },
  { iso: 'TR', name: 'Turkey', dial: '90', flag: '🇹🇷' },
  { iso: 'EG', name: 'Egypt', dial: '20', flag: '🇪🇬' },
  { iso: 'NG', name: 'Nigeria', dial: '234', flag: '🇳🇬' },
  { iso: 'KE', name: 'Kenya', dial: '254', flag: '🇰🇪' },
  { iso: 'RU', name: 'Russia', dial: '7', flag: '🇷🇺' },
  { iso: 'KR', name: 'South Korea', dial: '82', flag: '🇰🇷' },
];

const DEFAULT_ISO = 'IN';

/** Accepted phone format — matches the server's E.164 validation. */
export function isValidE164(v: string): boolean {
  return v === '' || /^\+[1-9]\d{6,14}$/.test(v);
}

// Parse an E.164 string back into a country + national number for editing.
// Picks the country whose dial code is the longest matching prefix.
function splitE164(value: string | null | undefined): { iso: string; national: string } {
  const digits = (value ?? '').replace(/\D/g, '');
  if (!digits) return { iso: DEFAULT_ISO, national: '' };
  const byLongestDial = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  const match = byLongestDial.find((c) => digits.startsWith(c.dial));
  if (match) return { iso: match.iso, national: digits.slice(match.dial.length) };
  return { iso: DEFAULT_ISO, national: digits };
}

export function PhoneInput({
  value,
  onChange,
  selectClassName,
  inputClassName,
  id,
}: {
  value: string;
  onChange: (e164: string) => void;
  selectClassName?: string;
  inputClassName?: string;
  id?: string;
}) {
  const initial = splitE164(value);
  const [iso, setIso] = useState(initial.iso);
  const [national, setNational] = useState(initial.national);

  const dialOf = (i: string) => COUNTRIES.find((c) => c.iso === i)?.dial ?? '91';

  function emit(nextIso: string, nextNational: string) {
    const digits = nextNational.replace(/\D/g, '');
    onChange(digits ? `+${dialOf(nextIso)}${digits}` : '');
  }

  const composed = national ? `+${dialOf(iso)}${national.replace(/\D/g, '')}` : '';
  const invalid = composed !== '' && !isValidE164(composed);

  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 'var(--inic-space-1)' }}>
      <span style={{ display: 'flex', gap: 'var(--inic-space-2)' }}>
        <select
          className={selectClassName}
          aria-label="Country code"
          value={iso}
          onChange={(e) => { setIso(e.target.value); emit(e.target.value, national); }}
          style={{ flex: '0 0 auto', maxWidth: 150 }}
        >
          {COUNTRIES.map((c) => (
            <option key={c.iso} value={c.iso}>{c.flag} +{c.dial} {c.name}</option>
          ))}
        </select>
        <input
          id={id}
          className={inputClassName}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder="98765 43210"
          value={national}
          onChange={(e) => { const n = e.target.value.replace(/[^\d]/g, ''); setNational(n); emit(iso, n); }}
          style={{ flex: 1 }}
        />
      </span>
      {invalid && (
        <span style={{ color: 'var(--inic-accent)', fontSize: 'var(--inic-size-2xs)' }}>
          Enter a valid phone number (7–15 digits including country code).
        </span>
      )}
    </span>
  );
}
